import type { LexicalEditor } from "lexical";
import { $getNodeByKey, $getRoot, $getSelection, $isElementNode, $isRangeSelection, type LexicalNode } from "lexical";
import {
  $getNodeTriplet,
  $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $getTableRowIndexFromTableCellNode,
  $isSimpleTable,
  $isTableNode,
  type TableCellNode,
  type TableRowNode,
} from "@lexical/table";
import type { TableCtx } from "./types";

/** 由已知 TableCellNode 计算 UI 几何（选区丢失时仍可用 cellKey 恢复） */
export function buildTableCtxFromCell(editor: LexicalEditor, cell: TableCellNode): TableCtx | null {
  const [, , table] = $getNodeTriplet(cell);
  if (!$isTableNode(table)) return null;
  const cellEl = editor.getElementByKey(cell.getKey());
  const tableEl = editor.getElementByKey(table.getKey());
  if (!cellEl || !tableEl) return null;
  const tr = cellEl.closest("tr");
  const rowRect = tr?.getBoundingClientRect() ?? cellEl.getBoundingClientRect();
  const rows = table.getChildren() as TableRowNode[];
  const firstRow = rows[0];
  const colCount = firstRow ? (firstRow.getChildren() as TableCellNode[]).length : 0;
  const colIndex = $getTableColumnIndexFromTableCellNode(cell);
  const tableDom =
    tableEl instanceof HTMLTableElement
      ? tableEl
      : (tableEl.querySelector("table") as HTMLTableElement | null);
  const tableRect = (tableDom ?? tableEl).getBoundingClientRect();
  let columnTopRect = cellEl.getBoundingClientRect();
  if (tableDom?.rows[0]) {
    const fr = tableDom.rows[0];
    if (colIndex >= 0 && colIndex < fr.cells.length) {
      columnTopRect = fr.cells[colIndex].getBoundingClientRect();
    }
  }
  return {
    cellKey: cell.getKey(),
    tableKey: table.getKey(),
    rowIndex: $getTableRowIndexFromTableCellNode(cell),
    colIndex,
    simple: $isSimpleTable(table),
    rowCount: rows.length,
    colCount,
    tableRect,
    cellRect: cellEl.getBoundingClientRect(),
    rowRect,
    columnTopRect,
  };
}

export function readTableCtx(editor: LexicalEditor): TableCtx | null {
  let out: TableCtx | null = null;
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    const cell = $getTableCellNodeFromLexicalNode(selection.anchor.getNode());
    if (!cell) return;
    out = buildTableCtxFromCell(editor, cell);
  });
  return out;
}

/** 焦点在手柄上、选区已不在单元格内时，用上次 cellKey 拉回几何 */
export function readTableCtxByCellKey(editor: LexicalEditor, cellKey: string): TableCtx | null {
  let out: TableCtx | null = null;
  editor.getEditorState().read(() => {
    const n = $getNodeByKey(cellKey as never);
    if (!n) return;
    let cell = $getTableCellNodeFromLexicalNode(n as never);
    if (!cell && n.getType() === "tablecell") cell = n as TableCellNode;
    if (!cell || cell.getKey() !== cellKey) return;
    out = buildTableCtxFromCell(editor, cell);
  });
  return out;
}

/** 遍历整棵树收集 `table` 节点 key（用于指针落在加行/加列条等表格外区域时的命中回退）。 */
export function collectTableNodeKeys(editor: LexicalEditor): string[] {
  const keys: string[] = [];
  editor.getEditorState().read(() => {
    const walk = (node: LexicalNode) => {
      if ($isTableNode(node as never)) keys.push(node.getKey());
      if ($isElementNode(node)) {
        for (const c of node.getChildren()) walk(c);
      }
    };
    walk($getRoot());
  });
  return keys;
}

/** 由表格节点 key 取与 `buildTableCtxFromCell` 一致的 `table` DOM 外接矩形（无选区时也可用）。 */
export function readTableRectByTableKey(editor: LexicalEditor, tableKey: string): DOMRect | null {
  let valid = false;
  editor.getEditorState().read(() => {
    const n = $getNodeByKey(tableKey as never);
    valid = !!n && $isTableNode(n as never);
  });
  if (!valid) return null;
  const el = editor.getElementByKey(tableKey);
  if (!el) return null;
  const tableDom =
    el instanceof HTMLTableElement ? el : (el.querySelector("table") as HTMLTableElement | null);
  return (tableDom ?? el).getBoundingClientRect();
}
